import React, { useCallback, useEffect, useState } from "react";
import { inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowDownCircle, ArrowUpCircle, Download, Landmark } from "lucide-react";
import { OnlinePayments } from "@/components/OnlinePayments";

const PERIODS = [{ k: "day", l: "Daily" }, { k: "week", l: "Weekly" }, { k: "month", l: "Monthly" }, { k: "year", l: "Yearly" }, { k: "all", l: "All time" }];
export const today = () => new Date().toLocaleDateString("en-CA");

const Total = ({ label, value, tone, testid }) => (
  <div className="rounded-lg border border-border/60 bg-card/60 p-3">
    <p className="eyebrow text-muted-foreground">{label}</p>
    <p className={`font-serif text-xl mt-1 ${tone}`} data-testid={testid}>{inr(value)}</p>
  </div>
);

export const AccountsView = ({ client }) => {
  const [period, setPeriod] = useState("month");
  const [date, setDate] = useState(today());
  const [d, setD] = useState({ rows: [], total_in: 0, total_out: 0, net: 0, by_type: {} });
  const load = useCallback(() => {
    client.get("/accounts/transactions", { params: { period, date } }).then((r) => setD(r.data)).catch(() => toast.error("Could not load transactions"));
  }, [client, period, date]);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    try {
      const r = await client.get("/accounts/transactions.xlsx", { params: { period, date }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `accounts_${period}_${date}.xlsx`; a.click();
    } catch { toast.error("Download failed"); }
  };
  return (
    <Card className="bg-card/40 border-gold/20 p-5" data-testid="accounts-view">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <p className="eyebrow text-gold flex items-center gap-1.5 mr-auto"><Landmark className="w-4 h-4" /> Money transactions</p>
        {PERIODS.map((p) => (
          <Button key={p.k} size="sm" variant={period === p.k ? "default" : "outline"} onClick={() => setPeriod(p.k)} data-testid={`accounts-period-${p.k}`}
            className={period === p.k ? "bg-gold text-[#0a2e22] h-8 text-xs" : "border-gold/30 text-gold h-8 text-xs"}>{p.l}</Button>
        ))}
        {period !== "all" && <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-8 bg-card/60" data-testid="accounts-date" />}
        <Button size="sm" onClick={download} data-testid="accounts-download" className="h-8 bg-gold text-[#0a2e22] hover:bg-gold/90 gap-1.5 text-xs"><Download className="w-3.5 h-3.5" /> Excel</Button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Total label="Money in" value={d.total_in} tone="text-emerald-300" testid="accounts-total-in" />
        <Total label="Money out" value={d.total_out} tone="text-red-300" testid="accounts-total-out" />
        <Total label="Net" value={d.net} tone={d.net >= 0 ? "text-gold" : "text-red-300"} testid="accounts-net" />
      </div>
      {Object.keys(d.by_type).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(d.by_type).map(([k, v]) => <span key={k} className="text-[11px] rounded-full border border-gold/30 px-2.5 py-1 text-muted-foreground">{k}: <b className="text-white">{inr(v)}</b></span>)}
        </div>
      )}
      <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#05261D]"><tr className="text-left">
            {["Date", "Type", "Details", "Amount"].map((h) => <th key={h} className="eyebrow text-gold px-2 py-2 border-b border-gold/30">{h}</th>)}
          </tr></thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={i} className="border-b border-border/40" data-testid={`accounts-row-${i}`}>
                <td className="px-2 py-2 text-xs whitespace-nowrap">{new Date(r.date).toLocaleDateString("en-IN")}</td>
                <td className="px-2 py-2 text-xs"><span className="inline-flex items-center gap-1">{r.flow === "in" ? <ArrowDownCircle className="w-3.5 h-3.5 text-emerald-300" /> : <ArrowUpCircle className="w-3.5 h-3.5 text-red-300" />}{r.type}</span></td>
                <td className="px-2 py-2 text-xs text-muted-foreground">{r.detail}</td>
                <td className={`px-2 py-2 font-semibold whitespace-nowrap ${r.flow === "in" ? "text-emerald-300" : "text-red-300"}`}>{r.flow === "in" ? "+" : "−"}{inr(r.amount)}</td>
              </tr>
            ))}
            {d.rows.length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-xs text-muted-foreground">No transactions in this period.</td></tr>}
          </tbody>
        </table>
      </div>
      <OnlinePayments client={client} period={period} date={date} />
    </Card>
  );
};
